import type { Org } from "@/lib/api/organizations";

// Local connects OAuth-style through the reverse proxy (self-signed cert) via
// the `mcp-remote` bridge: it opens a browser to authorize — no token. The proxy
// host must match the issuer/resource the server advertises (`https://localhost:8443`).
const LOCAL_PROXY_ORIGIN = "https://localhost:8443";
const LOCAL_HOSTS = ["localhost", "127.0.0.1", "0.0.0.0", "[::1]"];

/** Local connects to the MCP server through the dev reverse proxy and authorizes
 *  via OAuth in the browser (no PAT). Keyed off the host (not `import.meta.env.DEV`)
 *  so a local *production* build is still detected as local. Prod authorizes over
 *  HTTPS the same way, but native clients can skip the bridge. */
export const isLocalEnv = (): boolean =>
  LOCAL_HOSTS.includes(window.location.hostname);

export const isDevEnv = (): boolean =>
  /^(mcp-)?dev\.|-dev\./.test(window.location.hostname);

// Without an org the consent screen asks which one; `/org/{id}/mcp` locks it.
export const mcpServerUrl = (orgId?: string): string =>
  `${isLocalEnv() ? LOCAL_PROXY_ORIGIN : window.location.origin}${orgId ? `/org/${orgId}` : ""}/mcp`;

/** The org a connection is locked to; `null` leaves the choice to consent. */
export type OrgLock = Pick<Org, "org_id" | "slug" | "is_personal"> | null;

/** Local, dev and prod register under different keys so one client config can
 *  hold all three; a locked org adds its slug so two orgs coexist the same way
 *  and an agent can tell them apart. */
export const serverName = (lock: OrgLock): string => {
  const env = isLocalEnv() ? "-local" : isDevEnv() ? "-dev" : "";
  const org = lock ? `-${lock.is_personal ? "personal" : lock.slug}` : "";
  return `mycorr${env}${org}`;
};

// Shared `mcp-remote` bridge entry for config-file clients (Claude Desktop, Cursor,
// VS Code, …). TLS verification is disabled for the dev proxy's self-signed cert.
const localBridgeServer = (lock: OrgLock) => ({
  command: "npx",
  args: ["-y", "mcp-remote@latest", mcpServerUrl(lock?.org_id)],
  env: { NODE_TLS_REJECT_UNAUTHORIZED: "0" },
});

/// The named entry on its own, dedented — what you paste *into* a config that
/// already has an `mcpServers` (or VS Code `servers`) object. Emitting the whole
/// wrapper invites replacing a file that already has other servers in it.
const configEntry = (lock: OrgLock, entry: object): string =>
  JSON.stringify({ [serverName(lock)]: entry }, null, 2)
    .split("\n")
    .slice(1, -1)
    .map((line) => line.slice(2))
    .join("\n");

const utf8ToBase64 = (s: string): string =>
  btoa(unescape(encodeURIComponent(s)));

export const cursorDeepLink = (lock: OrgLock): string => {
  const config = utf8ToBase64(
    JSON.stringify({ url: mcpServerUrl(lock?.org_id) }),
  );
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${serverName(lock)}&config=${config}`;
};

export const vscodeDeepLink = (lock: OrgLock): string => {
  const obj = {
    name: serverName(lock),
    type: "http",
    url: mcpServerUrl(lock?.org_id),
  };
  return `vscode:mcp/install?${encodeURIComponent(JSON.stringify(obj))}`;
};

const bridgeArgs = (lock: OrgLock): string =>
  `npx -y mcp-remote@latest ${mcpServerUrl(lock?.org_id)}`;

export const claudeCodeCmd = (lock: OrgLock): string =>
  isLocalEnv()
    ? `claude mcp add ${serverName(lock)} --env NODE_TLS_REJECT_UNAUTHORIZED=0 -- ${bridgeArgs(lock)}`
    : `claude mcp add --transport http ${serverName(lock)} ${mcpServerUrl(lock?.org_id)}`;

export const claudeDesktopEntry = (lock: OrgLock): string =>
  configEntry(lock, localBridgeServer(lock));

export const codexCmd = (lock: OrgLock): string =>
  isLocalEnv()
    ? `codex mcp add ${serverName(lock)} --env NODE_TLS_REJECT_UNAUTHORIZED=0 -- ${bridgeArgs(lock)}`
    : `codex mcp add ${serverName(lock)} --url ${mcpServerUrl(lock?.org_id)}`;

// Gemini's default scope is the directory it runs in; user scope works from anywhere.
export const geminiCmd = (lock: OrgLock): string =>
  isLocalEnv()
    ? `gemini mcp add --scope user -e NODE_TLS_REJECT_UNAUTHORIZED=0 ${serverName(lock)} npx -- -y mcp-remote@latest ${mcpServerUrl(lock?.org_id)}`
    : `gemini mcp add --scope user --transport http ${serverName(lock)} ${mcpServerUrl(lock?.org_id)}`;

export const mcpServerEntry = (lock: OrgLock): string =>
  configEntry(
    lock,
    isLocalEnv()
      ? localBridgeServer(lock)
      : { url: mcpServerUrl(lock?.org_id) },
  );

export const vscodeServerEntry = (lock: OrgLock): string =>
  configEntry(
    lock,
    isLocalEnv()
      ? localBridgeServer(lock)
      : { type: "http", url: mcpServerUrl(lock?.org_id) },
  );
